import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useOrderNotifications = () => {
  
  const sendOrderNotification = async (
    orderId: string, 
    status: string, 
    customerName?: string,
    restaurantName?: string
  ) => {
    try {
      console.log(`🔔 Sending order notification: ${orderId} - ${status}`);
      
      // Get the order details and customer info
      const { data: order, error } = await supabase
        .from('orders')
        .select(`
          id,
          customer_id,
          delivery_type,
          customer_name,
          profiles!orders_customer_id_fkey(onesignal_player_id)
        `)
        .eq('id', orderId)
        .single();

      if (error || !order) {
        console.error('Error fetching order for notification:', error);
        return;
      }

      const playerId = order.profiles?.onesignal_player_id;
      if (!playerId) {
        console.log('No OneSignal player ID found for customer');
        return;
      }

      console.log(`📱 Found customer player ID: ${playerId}`);

      // Create notification message based on status
      let message = '';
      let heading = 'Order Update';

      switch (status) {
        case 'confirmed':
          message = `Great news! Your order has been confirmed and is being prepared.`;
          heading = 'Order Confirmed!';
          break;
        case 'ready_for_pickup':
          message = order.delivery_type === 'pickup' 
            ? `Your order is ready for pickup!`
            : `Your order is ready and waiting for a driver.`;
          heading = 'Order Ready!';
          break;
        case 'picked_up':
          message = `Your order has been picked up and is on the way to you!`;
          heading = 'Order On The Way!';
          break;
        case 'delivered':
          message = `Your order has been delivered. Enjoy your meal!`;
          heading = 'Order Delivered!';
          break;
        default:
          message = `Your order status has been updated to ${status}.`;
      }

      console.log(`📤 Sending push notification: ${heading} - ${message}`);

      // Send the notification
      await supabase.functions.invoke('send-push-notification', {
        body: {
          message,
          heading,
          userIds: [playerId]
        }
      });

      console.log(`✅ Order notification sent: ${heading} - ${message}`);
    } catch (error) {
      console.error('❌ Error sending order notification:', error);
    }
  };

  const sendDriverNotification = async (
    driverId: string,
    orderDetails: {
      orderId: string;
      restaurantName: string;
      customerName: string;
      deliveryAddress: string;
    }
  ) => {
    try {
      // Get the driver's player ID
      const { data: driver, error } = await supabase
        .from('profiles')
        .select('onesignal_player_id')
        .eq('id', driverId)
        .single();

      if (error || !driver?.onesignal_player_id) {
        console.log('No OneSignal player ID found for driver');
        return;
      }

      const message = `New delivery assigned! Pick up from ${orderDetails.restaurantName} for ${orderDetails.customerName}`;
      const heading = 'New Delivery Assignment';

      await supabase.functions.invoke('send-push-notification', {
        body: {
          message,
          heading,
          userIds: [driver.onesignal_player_id],
          data: {
            orderId: orderDetails.orderId,
            type: 'driver_assignment'
          }
        }
      });

      console.log('Driver notification sent:', heading);
    } catch (error) {
      console.error('Error sending driver notification:', error);
    }
  };

  const sendVendorNotification = async (
    restaurantId: string,
    orderDetails: {
      orderId: string;
      customerName: string;
      totalAmount: number;
    }
  ) => {
    try {
      // Get the restaurant owner's player ID
      const { data: restaurant, error } = await supabase
        .from('restaurants')
        .select(`
          vendor_id,
          name,
          profiles!restaurants_vendor_id_fkey(onesignal_player_id)
        `)
        .eq('id', restaurantId)
        .single();

      if (error || !restaurant?.profiles?.onesignal_player_id) {
        console.log('No OneSignal player ID found for vendor');
        return;
      }

      const message = `New order received! Order from ${orderDetails.customerName} - $${orderDetails.totalAmount}`;
      const heading = 'New Order!';

      await supabase.functions.invoke('send-push-notification', {
        body: {
          message,
          heading,
          userIds: [restaurant.profiles.onesignal_player_id],
          data: {
            orderId: orderDetails.orderId,
            type: 'new_order'
          }
        }
      });

      console.log('Vendor notification sent:', heading);
    } catch (error) {
      console.error('Error sending vendor notification:', error);
    }
  };

  return {
    sendOrderNotification,
    sendDriverNotification,
    sendVendorNotification
  };
};